"use client";

import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";

import { cn } from "@/lib/utils";

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>
>(({ className, value, max, ...props }, ref) => {
  // Radix and the indicator below used to read `value` independently, and they disagreed on any
  // input that isn't a finite number in [0, max]: Radix falls back to `indeterminate` (dropping
  // aria-valuenow and logging a console error) while the indicator still computed `value || 0`,
  // so a NaN arriving from an upstream mapper rendered a silently empty bar that reads as "0%
  // complete" rather than "unknown". Resolve it once here so the two can never diverge.
  const ceiling = Number.isFinite(Number(max)) && Number(max) > 0 ? Number(max) : 100;
  const resolved =
    value == null || !Number.isFinite(Number(value))
      ? null
      : Math.min(ceiling, Math.max(0, Number(value)));

  return (
    <ProgressPrimitive.Root
      ref={ref}
      value={resolved}
      max={ceiling}
      className={cn("relative h-2 w-full overflow-hidden rounded-full bg-primary/20", className)}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className="h-full bg-primary transition-all"
        style={{ width: `${((resolved ?? 0) / ceiling) * 100}%` }}
      />
    </ProgressPrimitive.Root>
  );
});
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
